import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getTheme } from "@insite/client-framework/Services/ContentService";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SlantedReactangle from "@tecumseh/Icons/SlantedReactangle";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    buttonText = "buttonText",
    image = "image",
    buttonLink = "buttonLink",
    slantLine = "slantLine",
    buttonType = "buttonType",
    openInNewTab = "openInNewTab",
    customCSS = "customCSS",
}
const enum slantLine {
    yes = "yes",
    no = "no",
}
const enum buttonType {
    masterfluxButton = "masterfluxButton",
    tecumsehButton = "tecumsehButton",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.buttonText]: string;
        [fields.image]: string;
        [fields.buttonLink]: string;
        [fields.slantLine]: slantLine;
        [fields.buttonType]: buttonType;
        [fields.openInNewTab]: boolean;
        [fields.customCSS]: string;
    };
}

export interface HomePageBannerStyle {
    bannerContainer?: InjectableCss;
    bannerTitle?: TypographyProps;
    bannerDescription?: InjectableCss;
    buttonWrapper?: InjectableCss;
    parent?: InjectableCss;
    childOne?: InjectableCss;
    childTwo?: InjectableCss;
    bannerTextContainer?: InjectableCss;
    button?: LinkPresentationProps;
    slantedLineIcon?: InjectableCss;
}

export const homePageBannerStyle: HomePageBannerStyle = {
    bannerContainer: {
        css: css`
            border-radius: 8px;
            max-width: 673px;
            width: 100%;
            position: relative;
            padding: 33px 0;
            top: 22px;
            border: 0.5px solid rgba(0, 0, 0, 0.5);
            /* border-top: 6px solid ${COLORS.primaryBlue} ; */
            border-top: 6px solid ${getColor("primary")}!important;
            box-shadow: 0px 2px 32px rgba(67, 67, 67, 0.5);
            opacity: 1;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.551109) 0%, rgba(255, 255, 255, 0.206403) 100%);
            backdrop-filter: blur(20px);

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 340px !important;
                        height: auto !important;
                        left: 0;
                        margin: auto;
                        top: 150px;
                        box-shadow: 0px 2px 32px rgba(67, 67, 67, 0.164909);
                    `,
                    css`
                        max-width: 400px !important;
                        height: auto !important;
                        left: 0;
                        margin: auto;
                        top: 120px;
                        box-shadow: 0px 2px 32px rgba(67, 67, 67, 0.164909);
                    `,
                    css`
                        max-width: 513px !important;
                        height: auto !important;
                        left: 0;
                        margin: auto;
                        top: 150px;
                        box-shadow: 0px 2px 32px rgba(67, 67, 67, 0.5);
                    `,
                    css`
                        left: 20px;
                    `,

                    null,
                ])}
        `,
    },
    bannerTextContainer: {
        css: css`
            padding-left: 38.5px;
            padding-right: 38.5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        padding-right: 17.5px;
                        padding-left: 17.5px;
                    `,
                    css`
                        width: 100%;
                        padding-right: 17.5px;
                        padding-left: 17.5px;
                    `,
                    css`
                        width: 100%;
                        padding-right: 17.5px;
                        padding-left: 17.5px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    bannerTitle: {
        variant: "h1",
        css: css`
            color: ${getColor("secondary")};
            font-family: ${TYPOGRAPHY.fontFamily.barlow};
            font-weight: ${TYPOGRAPHY.weight.light};
            text-transform: uppercase;
            margin: auto;
            line-height: ${TYPOGRAPHY.lineHeight.xlarger} !important;
            margin-bottom: 12px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.mLarge};
                        width: 100%;
                        line-height: ${TYPOGRAPHY.lineHeight.xbase} !important;
                        margin-bottom: 10px;
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.xbase};
                        width: 100%;
                        line-height: ${TYPOGRAPHY.lineHeight.xlarge}!important;
                        margin-bottom: 10px;
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.biggest};
                        width: 100%;
                        line-height: ${TYPOGRAPHY.lineHeight.xlarge} !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    bannerDescription: {
        css: css`
            color: ${COLORS.black};
            font-size: ${TYPOGRAPHY.size.larger};
            margin: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.base};
                        width: 100%;
                        margin-top: 10px;
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.larger};
                        width: 100%;
                        margin-top: 10px;
                    `,
                    css`
                        width: 100%;
                        font-size: ${TYPOGRAPHY.size.larger};
                        margin-top: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    slantedLineIcon: {
        css: css`
            margin-left: 10px;
            > svg {
                color: ${getColor("primary")};
                height: 35px;
                width: 35px;
            }
        `,
    },
};

const styles = homePageBannerStyle;

export const TECMainHeroBanner: FC<OwnProps> = ({ fields }) => {
    const backgroundStyle = `background-image: url(${fields.image});`;

    const bannerWrapper = {
        css: css`
            max-width: 100%;
            min-height: 366px !important;
            ${backgroundStyle};
            background-size: cover;
            height: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        ${backgroundStyle}
                        background-size:cover;
                    `,
                    css`
                        ${backgroundStyle}
                        background-size:cover;
                    `,
                    null,
                    null,
                    null,
                ])}
            ${fields.customCSS}
        `,
    };

    return (
        <>
            <StyledWrapper className="bannerWrapper-tecmain" {...bannerWrapper}>
                <StyledWrapper
                    css={css`
                        max-width: 1200px;
                        margin: auto;
                    `}
                >
                    <StyledWrapper {...styles.bannerContainer} id="TECmainbanner">
                        <StyledWrapper {...styles.bannerTextContainer}>
                            <StyledWrapper
                                css={css`
                                    display: flex;
                                `}
                            >
                                <Typography as="h1" {...styles.bannerTitle}>
                                    {fields.title}
                                    {fields.slantLine === "yes" ? (
                                        <Icon src={SlantedReactangle} size={35} {...styles.slantedLineIcon}></Icon>
                                    ) : (
                                        ""
                                    )}
                                </Typography>
                            </StyledWrapper>
                            <Typography as="p" {...styles.bannerDescription}>
                                {" "}
                                {parse(fields.description, parserOptions)}
                            </Typography>
                        </StyledWrapper>
                        {fields.buttonText && (
                            <StyledWrapper>
                                {fields.buttonType === "tecumsehButton" ? (
                                    <StyledWrapper className="TecLinkButton">
                                        <Link
                                            target={fields.openInNewTab ? "_blank" : undefined}
                                            href={fields.buttonLink}
                                            {...styles.button}
                                        >
                                            {parse(fields.buttonText, parserOptions)}
                                        </Link>
                                    </StyledWrapper>
                                ) : (
                                    <StyledWrapper className="MasterfluxLinkButton">
                                        <Link
                                            target={fields.openInNewTab ? "_blank" : undefined}
                                            href={fields.buttonLink}
                                            {...styles.button}
                                        >
                                            {parse(fields.buttonText, parserOptions)}
                                        </Link>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const defaultCustomCss = `&.bannerWrapper-tecmain {

}`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: TECMainHeroBanner,
    definition: {
        group: "Basic",
        displayName: "TECMainHeroBanner",
        fieldDefinitions: [
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Background Image",
                tab: basicTab,
            },
            {
                name: fields.title,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.slantLine,
                fieldType: "General",
                editorTemplate: "RadioButtonsField",
                defaultValue: "yes",
                options: [
                    {
                        displayName: "yes",
                        value: slantLine.yes,
                    },
                    {
                        displayName: "no",
                        value: slantLine.no,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.description,
                fieldType: "Translatable",
                editorTemplate: "RichTextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.buttonType,
                fieldType: "General",
                editorTemplate: "RadioButtonsField",
                defaultValue: "tecumsehButton",
                options: [
                    {
                        displayName: "tecumsehButton",
                        value: buttonType.tecumsehButton,
                    },
                    {
                        displayName: "masterfluxButton",
                        value: buttonType.masterfluxButton,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.buttonText,
                fieldType: "Translatable",
                editorTemplate: "RichTextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.buttonLink,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.openInNewTab,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: defaultCustomCss,
                tab: advancedTab,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
